import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Pusher, { Channel } from 'pusher-js';
import { useLayout } from '../../../providers/Layout.provider';
import { useInteracEmbedUrlFields } from '../../../providers/InteracEmbedURL.provider';
import { useToken } from '../../../providers/Token.provider';
import { usePaylink } from '../../../providers/Paylink.provider';
import { useFlexiblePayment } from '../../../providers/FlexiblePayment.provider';
import usePostPaylinkDecline from '../../../hooks/useApi/usePostPaylinkDecline';
import usePostPaylinkSetAccount from '../../../hooks/useApi/usePostPaylinkSetAccount';
import Error from './Error/Error';
import useStorage from '../../../hooks/useStorage';
import BrandingLogo from '../../UI/BrandingLogo/BrandingLogo';
import { LanguageSwitcher } from 'UI';

const EmailContainer: FunctionComponent = () => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Email' });
  const { layout, src, hasBackButton, color, height, positionX, positionY, hasChangeLanguage, setLayoutInfo } =
    useLayout();
  const { paymentType, partialPaymentValues, isFlexiblePayment, installmentDetails } = useFlexiblePayment();
  const [declinePaylinkResponse, declinePaylink] = usePostPaylinkDecline({});
  const [pusher, setPusher] = useState<Pusher>();
  const [channel, setChannel] = useState<Channel>();
  const { transactionId, interacEmbedUrl, setInteracEmbedUrl, setTransactionId } = useInteracEmbedUrlFields();
  const { IframeLayout, SeparatorCurve, SeparatorImg, Image, Box } = vp;
  const { token, isSetByBrowser } = useToken();
  const { Token, paylink } = usePaylink();
  const [currentState, setCurrentState] = useState<'loading' | 'closed' | 'errorBlock' | 'errorEtransfer'>('loading');
  const showBackButton = !isSetByBrowser;
  const [complete, setComplete] = useState(false);
  const storage = useStorage();
  const [paylinkSetAccountResponse, completePaylink] = usePostPaylinkSetAccount({});
  useEffect(() => {
    if (pusher) {
      setChannel(pusher.subscribe(`${transactionId}`));
    }
  }, [pusher, transactionId]);
  useEffect(() => {
    setPusher(
      new Pusher(import.meta.env.VITE_PUSHER_CONFIG_KEY, {
        cluster: 'us3',
      }),
    );
    return () => {
      if (pusher) {
        pusher.disconnect();
      }
    };
  }, []);
  const [newWindow, setNewWindow] = useState<Window | null>(null);
  const newWindowRef = useRef<Window | null>(null);

  const handleOpenNewWindow = () => {
    // Prevent opening a second popup if one is already open
    if (newWindowRef.current && !newWindowRef.current.closed) return;

    const newWindowInstance = window.open(
      interacEmbedUrl,
      '_blank',
      `width=${window.innerWidth * 0.9},height=${window.innerHeight * 0.9}`,
    );
    if (newWindowInstance) {
      newWindowRef.current = newWindowInstance;
      setNewWindow(newWindowInstance);
      setCurrentState('loading');
      const checkWindowClosed = setInterval(() => {
        if (newWindowInstance.closed || complete) {
          clearInterval(checkWindowClosed);
          newWindowRef.current = null;
          setNewWindow(null);
          window.focus();
          if (!complete) {
            setTimeout(() => {
              setCurrentState('closed');
            }, 1000);
          }
        }
      }, 1000);
    } else {
      setCurrentState('errorBlock');
    }
  };

  const focusNewWindow = () => {
    if (newWindow && !newWindow.closed) {
      newWindow.focus();
    }
  };

  useEffect(() => {
    handleOpenNewWindow();
  }, []);
  useEffect(() => {
    if (declinePaylinkResponse.Success) {
      history.push('/connect/request-failed');
    }
  }, [declinePaylinkResponse]);
  useEffect(() => {
    if (paylinkSetAccountResponse && paylinkSetAccountResponse.Success) {
      history.push('/thank-you', {
        paymentMethodType: 'email',
        paymentMethod: undefined,
      });
    } else if (paylinkSetAccountResponse && !paylinkSetAccountResponse.Success) {
      history.push('/connect/transaction-error');
    }
  }, [paylinkSetAccountResponse]);
  useEffect(() => {
    storage.paymentMethod.storePaymentMethod('interac');
  }, []);

  useEffect(() => {
    if (channel) {
      channel.bind('money-request-event', (_data: { message: string }) => {
        setComplete(true);
        if (newWindow && !newWindow.closed) {
          newWindow.close();
          window.focus();
        }
        if (_data.message === 'DECLINED') {
          declinePaylink({ SingleUseToken: Token });
        } else if (isFlexiblePayment) {
          if (paymentType === vp.FlexiblePaymentMethods.PartialPayment) {
            completePaylink({
              SingleUseToken: Token,
              PaymentMethod: 'email',
              FlexiblePaymentOptions: {
                PaymentOption: 'partial',
                PartialPaymentDetails: partialPaymentValues,
              },
            });
          }
          if (paymentType === vp.FlexiblePaymentMethods.InstallmentPlan) {
            completePaylink({
              SingleUseToken: Token,
              PaymentMethod: 'email',
              FlexiblePaymentOptions: {
                PaymentOption: 'installments',
                InstallmentDetails: installmentDetails,
              },
            });
          }
        } else {
          completePaylink({
            SingleUseToken: Token,
            PaymentMethod: 'email',
          });
        }
      });
    }
  }, [channel]);
  useEffect(() => {
    if (!interacEmbedUrl) {
      if (paylink.InteracEmbedUrl) {
        setInteracEmbedUrl(paylink.InteracEmbedUrl);
        setTransactionId(paylink.InteracTransactionID || '');
      }
    }
  }, [paylink.InteracEmbedUrl]);
  useEffect(() => {
    if (Token) {
      setLayoutInfo(
        vp.chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
        'curve',
        '',
        80,
        20,
        0,
        true && (currentState !== 'closed' || token?.PaymentSelectionMethod === 'email'),
        true,
      );
    } else {
      setLayoutInfo(
        vp.chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
        'curve',
        '',
        0,
        0,
        0,
        showBackButton && (currentState !== 'closed' || token?.PaymentSelectionMethod === 'email'),
        true,
      );
    }
  }, [currentState]);
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    const handlePopState = () => {
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    };
  }, [newWindow]);

  return (
    <IframeLayout
      separator={
        layout === 'img' ? (
          <SeparatorImg width={100} height={110} fill={color} />
        ) : layout === 'curve' ? (
          <SeparatorCurve width={140} height={110} fill={color} />
        ) : (
          <></>
        )
      }
      image={
        src === '' ? (
          <></>
        ) : (
          <Image width="auto" height={height} positionX={positionX} positionY={positionY} src={src} />
        )
      }
      hasBackButton={hasBackButton}
      topRight={hasChangeLanguage ? <LanguageSwitcher /> : undefined}
      content={
        <div>
          <Box jc="center">
            <Box flx="col" ai="center" maxwidth="545px">
              {token?.BrandingDetails?.Logo && (
                <Box pb="lg">
                  <BrandingLogo src={token?.BrandingDetails?.Logo} />
                </Box>
              )}

              <Box flx="col" pb="md" width="100%">
                {currentState === 'loading' && (
                  <Error
                    ErrorMessage={
                      <>
                        {' '}
                        <span>
                          <i>Interac</i>
                          <sup
                            style={{
                              position: 'relative',
                              top: '-0.2em',
                              right: '-0.1em',
                              fontSize: '0.6em',
                            }}
                          >
                            &reg;
                          </sup>
                        </span>{' '}
                        {t('loadingTitle')}
                      </>
                    }
                    SecondaryErrorMessage={
                      <Trans
                        i18nKey="Email.loadingMessage"
                        prefix="Email"
                        components={{
                          italic: <i />,
                          superscript: <sup />,
                        }}
                      />
                    }
                    ErrorButtonText={t('reopenWindow')}
                    TryAgain={() => {
                      focusNewWindow();
                    }}
                  />
                )}
                {currentState === 'errorBlock' && (
                  <Error
                    ErrorMessage={<>{t('errorBlock')}</>}
                    SecondaryErrorMessage={<>{t('errorBlockSecondary')}</>}
                    ErrorButtonText={t('reopenWindow')}
                    TryAgain={() => {
                      handleOpenNewWindow();
                    }}
                  />
                )}
                {currentState === 'closed' && (
                  <Error
                    ErrorMessage={
                      <>
                        {' '}
                        <span>
                          <i>Interac</i>
                          <sup
                            style={{
                              position: 'relative',
                              top: '-0.2em',
                              right: '-0.1em',
                              fontSize: '0.6em',
                            }}
                          >
                            &reg;
                          </sup>
                        </span>{' '}
                        {t('closedMessage')}
                      </>
                    }
                    SecondaryErrorMessage={
                      <Trans
                        i18nKey={
                          token?.PaymentSelectionMethod === 'email'
                            ? 'Email.closedSecondaryOnly'
                            : 'Email.closedSecondary'
                        }
                        components={{
                          italic: <i />,
                          superscript: <sup />,
                        }}
                      />
                    }
                    ErrorButtonText={t('reopenWindow')}
                    OtherButtonText={t('differentPaymentMethod') || undefined}
                    Other={
                      token?.PaymentSelectionMethod !== 'email'
                        ? () => {
                            history.goBack();
                          }
                        : undefined
                    }
                    TryAgain={() => {
                      handleOpenNewWindow();
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </div>
      }
    />
  );
};

export default EmailContainer;
